<template>
    <div class="flex h-[calc(100dvh-7.5rem)] flex-col">
        <div class="mb-4 flex items-center justify-between gap-2">
            <div>
                <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {{ t("ai.assistant.title") }}
                </h1>
                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {{ t("ai.assistant.description") }}
                </p>
            </div>
            <div class="flex items-center gap-2">
                <button
                    class="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 lg:hidden"
                    :title="t('ai.assistant.conversations')"
                    @click="showConversations = !showConversations"
                >
                    <MessageCircle class="h-4 w-4" />
                </button>
                <button
                    class="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700"
                    @click="startNewConversation"
                >
                    <Plus class="mr-2 h-4 w-4" />
                    {{ t("ai.assistant.newConversation") }}
                </button>
            </div>
        </div>

        <div class="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row">
            <aside
                class="flex max-h-56 w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 lg:max-h-none lg:w-64 lg:flex-shrink-0"
                :class="showConversations ? 'flex' : 'hidden lg:flex'"
            >
                <div
                    class="border-b border-gray-200 p-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400"
                >
                    {{ t("ai.assistant.conversations") }}
                </div>
                <div class="flex-1 overflow-y-auto">
                    <div
                        v-if="conversations.length === 0"
                        class="p-4 text-sm text-gray-500 dark:text-gray-400"
                    >
                        {{ t("ai.assistant.noConversations") }}
                    </div>
                    <button
                        v-for="conversation in conversations"
                        :key="conversation.id"
                        class="group w-full border-b border-gray-100 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/50"
                        :class="{
                            'bg-primary-50 dark:bg-primary-900/20':
                                conversation.id === conversationId,
                        }"
                        @click="openConversation(conversation.id)"
                    >
                        <div class="flex items-center justify-between gap-2">
                            <span
                                class="truncate text-sm font-medium text-gray-900 dark:text-white"
                            >
                                {{
                                    conversation.title ||
                                    t("ai.assistant.untitled")
                                }}
                            </span>
                            <span
                                class="text-gray-400 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
                                :title="t('common.delete')"
                                @click.stop="
                                    askDeleteConversation(conversation.id)
                                "
                            >
                                <Trash2 class="h-4 w-4" />
                            </span>
                        </div>
                        <span class="text-xs text-gray-500 dark:text-gray-400">
                            {{ formatDateTime(conversation.updated_at) }}
                        </span>
                    </button>
                </div>
            </aside>

            <div
                class="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
            >
                <div
                    ref="messagesContainer"
                    class="flex-1 space-y-4 overflow-y-auto p-4"
                >
                    <div
                        v-if="messages.length === 0"
                        class="flex h-full flex-col items-center justify-center px-6 text-center"
                    >
                        <Sparkles class="mb-4 h-12 w-12 text-primary-500" />
                        <p
                            class="mb-6 max-w-md text-gray-600 dark:text-gray-400"
                        >
                            {{ t("ai.assistant.welcome") }}
                        </p>
                        <div class="flex flex-wrap justify-center gap-2">
                            <button
                                v-for="suggestion in suggestions"
                                :key="suggestion"
                                class="rounded-full bg-gray-100 px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                                @click="sendMessage(suggestion)"
                            >
                                {{ suggestion }}
                            </button>
                        </div>
                    </div>

                    <div
                        v-for="(message, index) in messages"
                        :key="index"
                        class="group flex"
                        :class="
                            message.role === 'user'
                                ? 'justify-end'
                                : 'justify-start'
                        "
                    >
                        <div class="max-w-[85%]">
                            <div
                                class="rounded-2xl px-4 py-2.5 text-sm leading-relaxed"
                                :class="
                                    message.role === 'user'
                                        ? 'whitespace-pre-wrap rounded-br-sm bg-primary-600 text-white'
                                        : 'rounded-bl-sm bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white'
                                "
                            >
                                <MarkdownContent
                                    v-if="message.role === 'assistant'"
                                    :content="message.content"
                                />
                                <template v-else>{{
                                    message.content
                                }}</template>
                                <span
                                    v-if="
                                        message.role === 'assistant' &&
                                        toolsRunning &&
                                        index === messages.length - 1
                                    "
                                    class="ml-1 inline-flex items-center align-text-bottom text-xs text-gray-500 dark:text-gray-400"
                                >
                                    <Loader2
                                        class="mr-1 h-3 w-3 animate-spin"
                                    />
                                    {{ t("ai.assistant.checkingData") }}
                                </span>
                                <span
                                    v-else-if="
                                        message.role === 'assistant' &&
                                        streaming &&
                                        index === messages.length - 1
                                    "
                                    class="ml-0.5 inline-block h-4 w-2 animate-pulse bg-gray-500 align-text-bottom dark:bg-gray-300"
                                ></span>
                            </div>
                            <div
                                class="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500"
                                :class="
                                    message.role === 'user'
                                        ? 'justify-end'
                                        : 'justify-start'
                                "
                            >
                                <button
                                    type="button"
                                    class="rounded p-0.5 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-600 focus:opacity-100 group-hover:opacity-100 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                                    :title="t('ai.assistant.copy')"
                                    :disabled="!message.content"
                                    @click="copyMessage(message.content)"
                                >
                                    <Copy class="h-3 w-3" />
                                </button>
                                <span v-if="message.createdAt">
                                    {{ formatTime(message.createdAt) }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-if="errorMessage"
                    class="flex items-center justify-between border-t border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
                >
                    <span>{{ errorMessage }}</span>
                    <router-link
                        v-if="providerNotConfigured"
                        to="/ai-providers"
                        class="font-medium underline"
                    >
                        {{ t("ai.assistant.goToProviders") }}
                    </router-link>
                </div>

                <div class="border-t border-gray-200 p-3 dark:border-gray-700">
                    <form class="flex gap-2" @submit.prevent="sendMessage()">
                        <input
                            v-model="draft"
                            type="text"
                            :placeholder="t('ai.assistant.inputPlaceholder')"
                            :disabled="streaming"
                            class="flex-1 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        />
                        <button
                            type="submit"
                            :disabled="streaming || !draft.trim()"
                            class="inline-flex items-center justify-center rounded-xl bg-primary-600 px-4 py-2.5 text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <Send class="h-4 w-4" />
                        </button>
                        <button
                            v-if="streaming"
                            type="button"
                            class="inline-flex items-center justify-center rounded-xl bg-red-600 px-4 py-2.5 text-white transition-colors hover:bg-red-700"
                            :title="t('ai.assistant.stop')"
                            @click="stopStreaming"
                        >
                            <Square class="h-4 w-4" />
                        </button>
                    </form>
                    <p class="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                        {{ t("ai.assistant.disclaimer") }}
                    </p>
                </div>
            </div>
        </div>

        <DeleteConfirmationModal
            :show="showDeleteModal"
            :title="t('ai.assistant.deleteConfirmTitle')"
            :message="t('ai.assistant.deleteConfirmText')"
            @close="showDeleteModal = false"
            @confirm="deleteConversation"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
    Copy,
    Loader2,
    MessageCircle,
    Plus,
    Send,
    Sparkles,
    Square,
    Trash2,
} from "@lucide/vue";
import aiService from "@/api/ai";
import type { AiConversation, AiConversationMessage } from "@/api/ai";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import MarkdownContent from "@/components/MarkdownContent.vue";
import { useToastStore } from "@/stores/toastStore";
import dayjs from "dayjs";

const { t, locale } = useI18n();
const toastStore = useToastStore();

interface ChatMessage {
    role: "user" | "assistant";
    content: string;
    createdAt: string;
}

const conversations = ref<AiConversation[]>([]);
const conversationId = ref<string | null>(null);
const messages = ref<ChatMessage[]>([]);
const draft = ref("");
const streaming = ref(false);
// True while the model is running school-data tool calls mid-stream.
const toolsRunning = ref(false);
const errorMessage = ref<string | null>(null);
const providerNotConfigured = ref(false);
// Mobile starts with the list closed so the chat gets the full width; on
// desktop the aside ignores this state and is always rendered (lg:flex).
const showConversations = ref(false);
const messagesContainer = ref<HTMLElement | null>(null);
const abortController = ref<AbortController | null>(null);
const showDeleteModal = ref(false);
const conversationToDelete = ref<string | null>(null);

const suggestions = computed(() =>
    locale.value.startsWith("id")
        ? [
              "Kelas dan mapel apa saja yang saya ajar?",
              "Bagaimana hasil ujian terakhir saya?",
              "Siswa mana yang berisiko di kelas saya?",
              "Rekap absensi kelas saya bulan ini",
          ]
        : [
              "Which classes and subjects do I teach?",
              "How did my latest exam go?",
              "Which students are at risk in my classes?",
              "My classes attendance recap this month",
          ],
);

const formatDateTime = (value: string): string =>
    dayjs(value).format("DD MMM YYYY HH:mm");

const formatTime = (value: string): string => dayjs(value).format("HH:mm");

const copyMessage = async (content: string): Promise<void> => {
    if (!content) return;

    try {
        await navigator.clipboard.writeText(content);
        toastStore.success(t("ai.assistant.copied"));
    } catch (error) {
        console.error("Clipboard copy failed:", error);
        toastStore.error(t("ai.assistant.copyFailed"));
    }
};

const scrollToBottom = async (): Promise<void> => {
    await nextTick();

    if (messagesContainer.value) {
        messagesContainer.value.scrollTop =
            messagesContainer.value.scrollHeight;
    }
};

const fetchConversations = async (): Promise<void> => {
    try {
        conversations.value = await aiService.getConversations();
    } catch (error) {
        console.error("Failed to fetch conversations:", error);
    }
};

const startNewConversation = (): void => {
    conversationId.value = null;
    messages.value = [];
    errorMessage.value = null;
    providerNotConfigured.value = false;
};

const openConversation = async (id: string): Promise<void> => {
    conversationId.value = id;
    messages.value = [];
    errorMessage.value = null;
    // On mobile the list overlays the chat; picking one closes it. On
    // desktop the aside is always visible, so this is a no-op there.
    showConversations.value = false;

    try {
        const history: AiConversationMessage[] =
            await aiService.getMessages(id);
        messages.value = history.map((message) => ({
            role: message.role,
            content: message.content,
            createdAt: message.created_at,
        }));
        scrollToBottom();
    } catch (error) {
        console.error("Failed to load conversation:", error);
        toastStore.error(t("ai.assistant.loadFailed"));
    }
};

const askDeleteConversation = (id: string): void => {
    conversationToDelete.value = id;
    showDeleteModal.value = true;
};

const deleteConversation = async (): Promise<void> => {
    const id = conversationToDelete.value;

    if (!id) {
        return;
    }

    try {
        await aiService.deleteConversation(id);
        conversations.value = conversations.value.filter(
            (conversation) => conversation.id !== id,
        );

        if (conversationId.value === id) {
            startNewConversation();
        }

        toastStore.success(t("ai.assistant.conversationDeleted"));
    } catch (error) {
        console.error("Failed to delete conversation:", error);
        toastStore.error(t("ai.assistant.deleteFailed"));
    } finally {
        conversationToDelete.value = null;
        showDeleteModal.value = false;
    }
};

const stopStreaming = (): void => {
    abortController.value?.abort();
};

// Server-side tool steps can legitimately take a while, but a stream that
// stays completely silent holds a connection (and a worker thread) open
// forever. Drop it client-side when nothing arrives for this long.
const STREAM_IDLE_TIMEOUT_MS = 60_000;
let idleWatchdog: ReturnType<typeof setTimeout> | null = null;
let idleWatchdogTripped = false;

const clearIdleWatchdog = (): void => {
    if (idleWatchdog !== null) {
        clearTimeout(idleWatchdog);
        idleWatchdog = null;
    }
};

const armIdleWatchdog = (): void => {
    clearIdleWatchdog();

    idleWatchdog = setTimeout(() => {
        idleWatchdogTripped = true;
        abortController.value?.abort();
    }, STREAM_IDLE_TIMEOUT_MS);
};

const sendMessage = async (presetText?: string): Promise<void> => {
    const text = (presetText ?? draft.value).trim();

    if (!text || streaming.value) {
        return;
    }

    draft.value = "";
    errorMessage.value = null;
    providerNotConfigured.value = false;
    messages.value.push({
        role: "user",
        content: text,
        createdAt: new Date().toISOString(),
    });
    messages.value.push({ role: "assistant", content: "" });
    streaming.value = true;
    toolsRunning.value = false;
    scrollToBottom();

    abortController.value = new AbortController();
    const xsrfToken = aiService.xsrfToken();

    try {
        const response = await fetch(aiService.chatStreamUrl(), {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Accept: "text/event-stream",
                "X-Requested-With": "XMLHttpRequest",
                ...(xsrfToken ? { "X-XSRF-TOKEN": xsrfToken } : {}),
            },
            credentials: "same-origin",
            body: JSON.stringify({
                message: text,
                conversation_id: conversationId.value,
            }),
            signal: abortController.value.signal,
        });

        if (!response.ok) {
            let detail: string | null = null;

            try {
                const body = await response.json();
                detail =
                    body?.data?.provider?.[0] ||
                    body?.data?.api_key?.[0] ||
                    body?.data?.conversation_id?.[0] ||
                    body?.message;

                if (body?.data?.provider || body?.data?.api_key) {
                    providerNotConfigured.value = true;
                }
            } catch {
                detail = `${response.status} ${response.statusText}`;
            }

            messages.value.pop();
            errorMessage.value = detail || t("ai.assistant.requestFailed");
            streaming.value = false;

            return;
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        armIdleWatchdog();

        while (reader) {
            const { done, value } = await reader.read();

            if (done) {
                break;
            }

            armIdleWatchdog();
            buffer += decoder.decode(value, { stream: true });
            const chunks = buffer.split("\n\n");
            buffer = chunks.pop() ?? "";

            for (const chunk of chunks) {
                const line = chunk.trim();

                if (!line.startsWith("data: ")) {
                    continue;
                }

                const payload = line.slice(6);

                if (payload === "[DONE]") {
                    continue;
                }

                let event: {
                    type?: string;
                    delta?: string;
                    conversation_id?: string;
                    message?: string;
                    tool_name?: string;
                };

                try {
                    event = JSON.parse(payload);
                } catch {
                    continue;
                }

                if (event.type === "text_delta" && event.delta) {
                    toolsRunning.value = false;
                    messages.value[messages.value.length - 1].content +=
                        event.delta;
                    scrollToBottom();
                } else if (event.type === "tool_call") {
                    // The model is fetching school data — show it instead of
                    // a silent wait (each tool round trip takes seconds).
                    toolsRunning.value = true;
                    scrollToBottom();
                } else if (event.type === "tool_result") {
                    toolsRunning.value = true;
                } else if (event.type === "error") {
                    errorMessage.value =
                        event.message || t("ai.assistant.streamFailed");
                } else if (
                    event.type === "conversation" &&
                    event.conversation_id
                ) {
                    conversationId.value = event.conversation_id;
                }
            }
        }
    } catch (error) {
        if ((error as Error).name === "AbortError") {
            // The user pressed stop (silent) unless the idle watchdog
            // tripped — a stream that went silent gets an explicit error.
            if (idleWatchdogTripped) {
                errorMessage.value = t("ai.assistant.streamIdle");
            }
        } else {
            console.error("Assistant request failed:", error);
            errorMessage.value = t("ai.assistant.requestFailed");
        }
    } finally {
        clearIdleWatchdog();
        idleWatchdogTripped = false;
        streaming.value = false;
        toolsRunning.value = false;
        abortController.value = null;

        if (
            messages.value.length > 0 &&
            messages.value[messages.value.length - 1].content === ""
        ) {
            messages.value.pop();

            if (!errorMessage.value) {
                errorMessage.value = t("ai.assistant.emptyReply");
            }
        }

        await fetchConversations();
    }
};

onMounted(() => {
    fetchConversations();
});

// Leaving the page mid-stream must abort the request so the browser does
// not keep a dead connection open and the server releases the worker.
onBeforeUnmount(() => {
    stopStreaming();
    clearIdleWatchdog();
});
</script>
